import { useState } from "react";

import { type DialpadCoverage } from "../../lib/api";

import {
  fmtAvg,
  fmtDateOnly,
  fmtMissingRange,
  fmtNum,
  fmtTimeOfDay,
} from "./shared.helpers";
import {
  CollapsibleSection,
  type RetryAssessmentsControls,
  type RetryTranscriptsControls,
} from "./shared";

export function DialpadCoverageTile({
  coverage,
  isLoading,
  isFetching,
  onRefresh,
  retryTranscripts,
  retryAssessments,
}: {
  coverage: DialpadCoverage | undefined;
  isLoading: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  retryTranscripts: RetryTranscriptsControls | null;
  retryAssessments: RetryAssessmentsControls | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [assessedExpanded, setAssessedExpanded] = useState(false);
  const [missingAsmtExpanded, setMissingAsmtExpanded] = useState(false);
  const [readyExpanded, setReadyExpanded] = useState(false);

  const coverageAutoExpand =
    !!retryTranscripts?.errorMessage ||
    !!retryTranscripts?.isRunning ||
    !!retryAssessments?.errorMessage ||
    !!retryAssessments?.isRunning;

  return (
    <CollapsibleSection
      title="Data Coverage"
      ariaLabel="Data coverage"
      subtitle="Dialpad"
      autoExpand={coverageAutoExpand}
      headerActions={
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          className="rounded border border-input bg-card text-foreground text-xs px-2.5 py-1 hover:bg-muted/40 disabled:opacity-50"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      }
    >
      {isLoading && !coverage ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          Loading coverage…
        </div>
      ) : !coverage || !coverage.has_calls ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          No calls synced yet. Use “Sync call data” above to fetch a
          range from Dialpad.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-border">
          {/* Date coverage */}
          <div className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Date coverage
            </div>
            <div className="text-sm text-foreground">
              <span className="font-medium">{fmtDateOnly(coverage.date_coverage.first_call_at)}</span>
              <span className="text-muted-foreground"> → </span>
              <span className="font-medium">{fmtDateOnly(coverage.date_coverage.last_call_at)}</span>
            </div>
            <div className="text-sm text-foreground tabular-nums">
              <span className="font-medium">{fmtNum(coverage.date_coverage.days_with_calls)}</span>
              <span className="text-muted-foreground"> / {fmtNum(coverage.date_coverage.total_days)} days have calls</span>
            </div>
            {coverage.date_coverage.missing_days === 0 ? (
              <div className="text-xs text-primary">No gaps in this range.</div>
            ) : (
              <div className="space-y-1">
                <div className="text-sm text-foreground">
                  <span className="font-medium tabular-nums">
                    {fmtNum(coverage.date_coverage.missing_days)}
                  </span>{" "}
                  missing day{coverage.date_coverage.missing_days === 1 ? "" : "s"}
                  {coverage.date_coverage.largest_gap && (
                    <span className="text-muted-foreground">
                      , largest gap {fmtMissingRange(coverage.date_coverage.largest_gap)}
                    </span>
                  )}
                </div>
                {coverage.date_coverage.missing_ranges.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-xs text-primary hover:underline"
                  >
                    {expanded ? "Hide details" : "Show details"}
                  </button>
                )}
                {expanded && (
                  <ul className="mt-1 max-h-48 overflow-auto text-xs text-muted-foreground space-y-0.5 list-disc ml-5">
                    {coverage.date_coverage.missing_ranges.map((r, i) => (
                      <li key={i}>{fmtMissingRange(r)}</li>
                    ))}
                    {coverage.date_coverage.truncated && (
                      <li className="list-none text-muted-foreground italic">
                        and{" "}
                        {coverage.date_coverage.total_missing_ranges -
                          coverage.date_coverage.missing_ranges.length}{" "}
                        more…
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Missing transcripts */}
          <div className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Missing transcripts
            </div>
            <div className="text-2xl font-semibold tabular-nums text-foreground">
              {fmtNum(coverage.missing_transcripts.count)}
            </div>
            {coverage.missing_transcripts.count === 0 ? (
              <div className="text-xs text-muted-foreground">
                Every viable call has a transcript.
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">
                  Viable calls without a stored transcript.
                </div>
                {coverage.missing_transcripts.first_at &&
                  coverage.missing_transcripts.last_at && (
                    <div className="text-xs text-muted-foreground">
                      Spans {fmtDateOnly(coverage.missing_transcripts.first_at)}
                      {" → "}
                      {fmtDateOnly(coverage.missing_transcripts.last_at)}
                    </div>
                  )}
              </>
            )}
            {retryTranscripts && (
              <div className="pt-1 space-y-1">
                <button
                  type="button"
                  onClick={retryTranscripts.onRetry}
                  disabled={
                    retryTranscripts.isPending ||
                    retryTranscripts.isRunning ||
                    coverage.missing_transcripts.count === 0
                  }
                  className="inline-flex items-center gap-1 rounded border border-input bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Fetch transcript + AI recap from Dialpad for every viable call that doesn't have a transcript row yet. Runs the same Phase 2 fetch the window sync uses."
                >
                  {retryTranscripts.isRunning || retryTranscripts.isPending
                    ? "Retrying…"
                    : "Retry missing transcripts"}
                </button>
                {retryTranscripts.isRunning && retryTranscripts.active && (
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {(() => {
                      const a = retryTranscripts.active;
                      const done =
                        (a.fetched ?? 0) + (a.missing ?? 0) + (a.errors ?? 0);
                      const total = a.candidates ?? 0;
                      return `Processed ${fmtNum(done)} / ${fmtNum(total)}`;
                    })()}
                    {(retryTranscripts.active.errors ?? 0) > 0 && (
                      <span className="text-destructive">
                        {" "}
                        ({fmtNum(retryTranscripts.active.errors!)} errors)
                      </span>
                    )}
                  </div>
                )}
                {retryTranscripts.errorMessage && (
                  <div className="text-xs text-destructive">
                    {retryTranscripts.errorMessage}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Ready for assessment (T10) */}
          <div className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Ready for assessment
            </div>
            <div className="text-2xl font-semibold tabular-nums text-foreground">
              {fmtNum(coverage.ready_for_assessment.count)}
            </div>
            {coverage.ready_for_assessment.count === 0 ? (
              <div className="text-xs text-muted-foreground">
                No downloaded calls are waiting on an assessment.
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">
                  Downloaded calls with a transcript whose agent is on a team
                  with an assessment, not yet assessed.
                </div>
                {coverage.ready_for_assessment.ranges.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setReadyExpanded((v) => !v)}
                    className="text-xs text-primary hover:underline"
                  >
                    {readyExpanded ? "Hide details" : "Show details"}
                  </button>
                )}
                {readyExpanded && (
                  <ul className="mt-1 max-h-48 overflow-auto text-xs text-muted-foreground space-y-0.5 list-disc ml-5">
                    {coverage.ready_for_assessment.ranges.map((r, i) => (
                      <li key={i}>{fmtMissingRange(r)}</li>
                    ))}
                    {coverage.ready_for_assessment.truncated && (
                      <li className="list-none text-muted-foreground italic">
                        and{" "}
                        {coverage.ready_for_assessment.total_ranges -
                          coverage.ready_for_assessment.ranges.length}{" "}
                        more…
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Failed assessments */}
          <div className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Failed assessments
            </div>
            <div className="text-2xl font-semibold tabular-nums text-foreground">
              {fmtNum(coverage.failed_assessments.count)}
            </div>
            {coverage.failed_assessments.count === 0 ? (
              <div className="text-xs text-muted-foreground">
                No failed assessment pairs.
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">
                  Assessment pairs that were attempted but failed.
                </div>
                {coverage.failed_assessments.failed_ranges.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setMissingAsmtExpanded((v) => !v)}
                    className="text-xs text-primary hover:underline"
                  >
                    {missingAsmtExpanded ? "Hide details" : "Show details"}
                  </button>
                )}
                {missingAsmtExpanded && (
                  <ul className="mt-1 max-h-48 overflow-auto text-xs text-muted-foreground space-y-0.5 list-disc ml-5">
                    {coverage.failed_assessments.failed_ranges.map((r, i) => (
                      <li key={i}>{fmtMissingRange(r)}</li>
                    ))}
                    {coverage.failed_assessments.truncated && (
                      <li className="list-none text-muted-foreground italic">
                        and{" "}
                        {coverage.failed_assessments.total_failed_ranges -
                          coverage.failed_assessments.failed_ranges.length}{" "}
                        more…
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
            {retryAssessments && (
              <div className="pt-1 space-y-1">
                <button
                  type="button"
                  onClick={retryAssessments.onRetry}
                  disabled={
                    retryAssessments.isPending ||
                    retryAssessments.isRunning ||
                    coverage.failed_assessments.count === 0
                  }
                  className="inline-flex items-center gap-1 rounded border border-input bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Enqueue an assessment run that re-grades every failed (call, assessment) pair. Successful pairs are not re-run."
                >
                  {retryAssessments.isRunning || retryAssessments.isPending
                    ? "Retrying…"
                    : "Retry failed assessments"}
                </button>
                {retryAssessments.errorMessage && (
                  <div className="text-xs text-destructive">
                    {retryAssessments.errorMessage}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sync history summary — kept last so it acts as the
              roll-up at the end of the Data Coverage row. */}
          <div className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Sync history summary
            </div>
            {coverage.sync_summary.days_covered === 0 ? (
              <div className="text-xs text-muted-foreground">
                No completed sync windows yet.
              </div>
            ) : (
              <>
                <div className="text-sm text-foreground">
                  <span className="font-medium">
                    {fmtDateOnly(coverage.sync_summary.covered_start)}
                  </span>
                  <span className="text-muted-foreground"> → </span>
                  <span className="font-medium">
                    {fmtDateOnly(coverage.sync_summary.covered_end)}
                  </span>
                </div>
                <div className="text-sm text-foreground tabular-nums">
                  <span className="font-medium">
                    {fmtNum(coverage.sync_summary.days_covered)}
                  </span>{" "}
                  days
                </div>
                <div className="text-sm text-foreground tabular-nums">
                  <span className="font-medium">
                    {fmtNum(coverage.sync_summary.total_calls)}
                  </span>{" "}
                  calls
                </div>
                <div className="text-sm text-foreground tabular-nums">
                  <span className="font-medium">
                    {fmtNum(coverage.sync_summary.transcripts)}
                  </span>{" "}
                  transcripts
                </div>
                <div className="text-sm text-foreground tabular-nums">
                  <span className="font-medium">
                    {fmtNum(coverage.sync_summary.assessed_calls)}
                  </span>{" "}
                  calls assessed
                </div>
                {coverage.sync_summary.assessed_calls === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    No calls have an assessment result yet.
                  </div>
                ) : coverage.sync_summary.assessed_ranges.length === 0 ? null : coverage
                    .sync_summary.assessed_ranges.length === 1 &&
                  !coverage.sync_summary.assessed_truncated ? (
                  <div className="text-sm text-foreground">
                    <span className="text-muted-foreground">
                      Assessment date range:{" "}
                    </span>
                    <span className="font-medium">
                      {(() => {
                        const soleRange =
                          coverage.sync_summary.assessed_ranges[0];
                        return soleRange
                          ? fmtMissingRange(soleRange)
                          : null;
                      })()}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-sm text-foreground">
                      <span className="text-muted-foreground">
                        Assessment date range across{" "}
                      </span>
                      <span className="font-medium tabular-nums">
                        {fmtNum(coverage.sync_summary.assessed_total_ranges)}
                      </span>
                      <span className="text-muted-foreground"> runs</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAssessedExpanded((v) => !v)}
                      className="text-xs text-primary hover:underline"
                    >
                      {assessedExpanded ? "Hide details" : "Show details"}
                    </button>
                    {assessedExpanded && (
                      <ul className="mt-1 max-h-48 overflow-auto text-xs text-muted-foreground space-y-0.5 list-disc ml-5">
                        {coverage.sync_summary.assessed_ranges.map((r, i) => (
                          <li key={i}>{fmtMissingRange(r)}</li>
                        ))}
                        {coverage.sync_summary.assessed_truncated && (
                          <li className="list-none text-muted-foreground italic">
                            and{" "}
                            {coverage.sync_summary.assessed_total_ranges -
                              coverage.sync_summary.assessed_ranges.length}{" "}
                            more…
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
                <div className="text-xs text-muted-foreground tabular-nums">
                  avg {fmtAvg(coverage.sync_summary.avg_calls_per_day)}/day ·{" "}
                  {fmtAvg(coverage.sync_summary.avg_viable_per_day)}/day viable
                  {coverage.sync_summary.viable_pct != null && (
                    <> ({coverage.sync_summary.viable_pct.toFixed(0)}% viable)</>
                  )}
                </div>
                {(coverage.sync_summary.avg_first_call_seconds != null ||
                  coverage.sync_summary.avg_last_call_seconds != null) && (
                  <div className="text-xs text-muted-foreground tabular-nums">
                    first call of the day avg{" "}
                    <span className="text-foreground">
                      {fmtTimeOfDay(coverage.sync_summary.avg_first_call_seconds)}
                    </span>
                    , last call ends avg{" "}
                    <span className="text-foreground">
                      {fmtTimeOfDay(coverage.sync_summary.avg_last_call_seconds)}
                    </span>{" "}
                    (NZ time)
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}
